import React, {useEffect, useMemo, useState} from 'react';
import {
    Button,
    Box,
    Checkbox,
    Portal,
    createListCollection,
    HStack,
    Input,
    Select,
    Spinner,
    VStack,
    Switch
} from '@chakra-ui/react';
import {useForm} from '@/uses/useForm';
import {useApi} from '@/uses/useApi';
import {Field, LanguageField, RichText} from "@/components";
import {ArticleFilesInput} from "@/components/input/ArticleFilesInput";
import {useLanguage} from "@/contexts/languageContext";
import DateTimePicker from "@/components/input/DateTimePicker";
import {ArticlePreview} from '@/components/article/ArticlePreview';

const VIDEO_PATTERNS = [
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]+)/,
    /youtu\.be\/([a-zA-Z0-9_-]+)/,
    /dailymotion\.com\/video\/([a-zA-Z0-9]+)/,
    /dai\.ly\/([a-zA-Z0-9]+)/,
    /vimeo\.com\/(\d+)/
];

const isVideoUrl = (url) => {
    if (!url) return false;
    return VIDEO_PATTERNS.some(pattern => pattern.test(url));
};

const convertToNewFilesFormat = (files) => {
    if (!files) return [];
    // If already in new format (array of objects), return as is
    if (typeof files[0] === 'object') return files;
    // Convert old format (array of strings) to new format
    return files.map(content => ({
        type: isVideoUrl(content) ? "video" : "image",
        name: isVideoUrl(content) ? "Vidéo" : "Image",
        content
    }));
};

export const CreateOrEditArticle = (props) => {
    const articleId = props?.id;

    const {t} = useLanguage();
    const [state, setState] = useState(-1);
    const {getLangObject, language} = useLanguage();
    const [article, setArticle] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [tags, setTags] = useState(null);
    const [defaultLanguage, setDefaultLanguage] = useState(null);
    const api = useApi();
    const {formData, formFieldErrors, handleInputChange, handleInputChanges, handleFormSubmit} = useForm(
        {
            files: [],
            title: getLangObject(),
            content: getLangObject(),
            tags: [],
            private: false,
        },
        {
            title: {
                custom: {
                    function: (value) => {
                        return Object.values(value).some((value) => value && value.length >= 3)
                    },
                    message: t('errors.pleaseFillFieldAllLanguages')
                },
            },
            content: {
                custom: {
                    function: (value) => {
                        return Object.values(value).some((value) => value && value.length >= 20)
                    },
                    message: t('errors.pleaseFillFieldAllLanguages')
                },
            },
        }
    );

    useEffect(() => {
        setState(() => 0);
        api.ApiService.get('tag', {
            getAll: 'bool:true'
        })
            .then((res) => {
                if (res?.data?.type === 'success' && res?.data?.value) {
                    setTags(() => res?.data?.value ?? []);
                }
            })
        if (articleId) {
            api.ApiService.getOne('article', articleId, {
                attributes: ['id', 'thumbnail', 'title', 'content', 'private', 'deletedAt', 'files', 'publishedAt'],
                include: [
                    {
                        model: 'user',
                        as: 'user',
                        attributes: ['id', 'firstname', 'lastname'],
                    },
                    {
                        model: 'tag',
                        as: 'tags',
                        attributes: ['id'],
                    },
                ],
            })
                .then((res) => {
                    if (res?.data?.type === 'success' && res?.data?.value) {
                        setArticle(() => res.data.value);
                    }
                })
        }
    }, []);

    useEffect(() => {
        if (article !== null) {
            handleInputChanges({
                ...article,
                files: convertToNewFilesFormat(article.newFiles || article.files),
                tags: article.tags.map(({id}) => id)
            });

            setDefaultLanguage(article.title.fr.length > 0 ? 'fr' : 'en')
        }
        /** If tags are retrieved, and we're in create mode
         * or article and tags are retrieved, and we're in edit mode
         */
        if ((!articleId || article !== null) && tags !== null) {
            setState(() => 1)
        }
    }, [article, tags, articleId]);

    const tagsCollection = useMemo(() => {
        return createListCollection({
            items: (tags ?? []).map((e) => ({
                label: e?.[`${language}Label`],
                value: e.id
            }))
        })
    }, [tags])

    return (
        <VStack h="100%" overflow="hidden" p="1.5rem 0" gap="5vh" w="100%">
            {
                state === 1 ?
                    <VStack overflow="auto" flex={1} gap="5" p="5" w="100%" align="stretch" justify="flex-start">
                        <VStack flex={1} justifyContent="flex-start" alignItems="flex-start" gap="1rem">
                            <LanguageField
                                id="title"
                                errors={formFieldErrors.title}
                                label={t('wrappers.mandatoryLabel', { value: t('fields.title') })}
                                defaultLanguage={defaultLanguage}
                                renderElement={(lang) => <Input
                                    value={formData.title[lang]}
                                    onChange={(e) => {
                                        handleInputChange('title', {
                                            ...formData.title,
                                            [lang]: e?.target?.value ?? ''
                                        })
                                    }}
                                    placeholder={t('articles.articleTitle')}
                                />}
                            />
                            <Field
                                label={t('wrappers.nonMandatoryLabel', { value: t('fields.tag_other') })}
                                errors={formFieldErrors.tags}
                            >
                                <Select.Root
                                    multiple
                                    collection={tagsCollection}
                                    defaultValue={formData.tags}
                                    onValueChange={e => handleInputChange('tags', e.value)}
                                >
                                    <Select.HiddenSelect/>
                                    <Select.Control>
                                        <Select.Trigger>
                                            <Select.ValueText
                                                placeholder={t('sentences.selectEntities', {entity: t('objects.tag_other')})}/>
                                        </Select.Trigger>
                                        <Select.IndicatorGroup>
                                            <Select.Indicator/>
                                        </Select.IndicatorGroup>
                                    </Select.Control>
                                    <Portal>
                                        <Select.Positioner>
                                            <Select.Content>
                                                {tagsCollection.items.map((tag) => (
                                                    <Select.Item item={tag} key={tag.value}>
                                                        {tag.label}
                                                        <Select.ItemIndicator/>
                                                    </Select.Item>
                                                ))}
                                            </Select.Content>
                                        </Select.Positioner>
                                    </Portal>
                                </Select.Root>
                            </Field>
                            <HStack
                                w="100%"
                                display="grid"
                                gridTemplateColumns="1fr 1fr"
                            >
                                <Field
                                    label={t('wrappers.nonMandatoryLabel', { value: t('fields.private') })}
                                    errors={formFieldErrors.private}
                                >
                                    <Switch.Root
                                        size="lg"
                                        id="private"
                                        checked={formData.private}
                                        onCheckedChange={(e) => handleInputChange('private', Boolean(e?.checked))}
                                    >
                                        <Switch.HiddenInput/>
                                        <Switch.Control/>
                                        <Switch.Label/>
                                    </Switch.Root>
                                </Field>
                                <Field
                                    justifySelf="end"
                                    w="auto"
                                >
                                    <DateTimePicker
                                        selected={formData.publishedAt ? new Date(formData.publishedAt) : null}
                                        onChange={(date) => {
                                            handleInputChange('publishedAt', date);
                                        }}
                                        label={t('fields.publicationDate')}
                                    />
                                </Field>
                            </HStack>
                            <ArticleFilesInput
                                files={formData.files}
                                onChange={(files) => handleInputChange('files', files)}
                            />
                            <LanguageField
                                id="content"
                                flex={1}
                                errors={formFieldErrors.content}
                                label={t('wrappers.mandatoryLabel', { value: t('fields.content') })}
                                defaultLanguage={defaultLanguage}
                                renderElement={(lang) => <RichText
                                    initialValue={formData.content[lang]}
                                    onChange={(e) => {
                                        /** Allow to set new content based on previousData without passing formData, that will cause re-render */
                                        handleInputChange('content', (prevData) => ({...prevData.content, [lang]: e}))
                                    }}
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '100%',
                                        overflow: 'hidden'
                                    }}
                                    className={"scrollable"}
                                    placeholder={t('articles.articleContent')}
                                />}
                            />
                        </VStack>
                    </VStack>
                        :
                        <VStack flex={1} w="100%" align="center" justify="flex-start">
                            <Spinner size="lg"/>
                        </VStack>
                        }
                        <HStack gap="2" mt="auto">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowPreview(!showPreview)}
                            >
                                {showPreview ? t('actions.hidePreview') : t('actions.preview')}
                            </Button>
                            <Button
                                variant="primary"
                                height="auto"
                                borderRadius=".5rem"
                                style={{padding: '1rem 1.5rem'}}
                                onClick={() => handleFormSubmit(async (data) => {
                                    const convertedData = {
                                        ...data,
                                        files: convertToNewFilesFormat(data.files)
                                    };
                                    if (articleId) {
                                        return api.ApiService.update('article', articleId, convertedData);
                                    }
                                    return api.ApiService.create('article', convertedData);
                                }, props?.successCallback)
                                }
                            >
                                {t('actions.validate')}
                            </Button>
                        </HStack>
                    {showPreview && (
                        <Box
                            position="fixed"
                            top="0"
                            left="0"
                            right="0"
                            bottom="0"
                            zIndex={1000}
                            bg="blackAlpha.700"
                            onClick={() => setShowPreview(false)}
                        >
                            <Box
                                position="fixed"
                                top="50%"
                                left="50%"
                                transform="translate(-50%, -50%)"
                                w="390px"
                                h="844px"
                                maxW="100vw"
                                maxH="90vh"
                                display="flex"
                                flexDirection="column"
                                borderRadius="xl"
                                boxShadow="2xl"
                                overflow="hidden"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Box flex={1} overflow="hidden">
                                    <ArticlePreview
                                        title={formData.title}
                                        content={formData.content}
                                        files={formData.files}
                                        publishedAt={formData.publishedAt}
                                    />
                                </Box>
                                    <Button
                                    marginTop={"10px"}
                                    variant="ghost"
                                    w="100%"
                                    h="50px"
                                    flexShrink={0}
                                    bg="white"
                                    borderRadius={"0"}
                                    onClick={() => setShowPreview(false)}
                                >
                                    {t('actions.close')}
                                </Button>
                            </Box>
                        </Box>
                    )}
                    </VStack>
                );
            };

            export default CreateOrEditArticle;
